import { useMemo } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { listPlans, plansByHotelKey, type MonthlyPlan } from '../features/plans/api.js';
import { PLAN_STATUS_LABEL } from '../features/plans/plan-status.js';

const HU_MONTHS = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

/**
 * A plan is considered archived once its `endDate` is strictly before
 * today (calendar date, not timestamp). A plan covering 2026-05-01 →
 * 2026-05-18 enters the archive on 2026-05-19. The explicit
 * `status='archived'` flag is also honored — those plans are forced
 * archived regardless of date.
 */
function isArchivedPlan(plan: MonthlyPlan, todayIso: string): boolean {
  if (plan.status === 'archived') return true;
  if (!plan.endDate) {
    // Legacy plans without an explicit endDate — fall back to (year,month).
    const lastDay = new Date(Date.UTC(plan.year, plan.month, 0));
    return lastDay.toISOString().slice(0, 10) < todayIso;
  }
  return plan.endDate < todayIso;
}

function HotelArchivePage() {
  const { hotelId } = Route.useParams();
  const plansQ = useQuery<MonthlyPlan[]>({
    queryKey: plansByHotelKey(hotelId),
    queryFn: () => listPlans(hotelId),
  });

  const todayIso = new Date().toISOString().slice(0, 10);

  const archived = useMemo(() => {
    return (plansQ.data ?? [])
      .filter((p) => isArchivedPlan(p, todayIso))
      .sort((a, b) => {
        // Newest archive first (most-recently-ended).
        const aEnd = a.endDate ?? `${a.year}-${String(a.month).padStart(2, '0')}-31`;
        const bEnd = b.endDate ?? `${b.year}-${String(b.month).padStart(2, '0')}-31`;
        return bEnd.localeCompare(aEnd);
      });
  }, [plansQ.data, todayIso]);

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
            Archív
          </div>
          <h2 className="mt-1 text-h2 font-medium leading-none tracking-tight text-[var(--ink-700)]">
            Archív tervek
          </h2>
          <p className="mt-1.5 text-meta text-[var(--fg-2)]">
            A már lejárt időszakra szóló vagy kézzel archivált tervek.
          </p>
        </div>
        <div className="text-meta text-[var(--fg-faint)]">{archived.length} terv</div>
      </header>

      {plansQ.isLoading ? (
        <p className="text-body text-[var(--fg-2)]">Betöltés…</p>
      ) : archived.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--app-divider)] py-12 text-center">
          <p className="text-body text-[var(--fg-2)]">Még nincs archivált terv.</p>
          <p className="mt-1 text-meta text-[var(--fg-faint)]">
            Egy terv automatikusan ide kerül a periódusa lejártát követő napon.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)]">
          <div className="grid grid-cols-[140px_1fr_120px_140px_24px] items-center gap-4 border-b border-[var(--app-divider)] bg-[var(--app-bg)] px-5 py-2.5 text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
            <span>Hónap</span>
            <span>Időszak</span>
            <span>Állapot</span>
            <span>Lejárt</span>
            <span />
          </div>
          <ul className="divide-y divide-[var(--app-divider)]">
            {archived.map((plan) => {
              const startsOn =
                plan.startDate ?? `${plan.year}-${String(plan.month).padStart(2, '0')}-01`;
              const endsOn =
                plan.endDate ?? `${plan.year}-${String(plan.month).padStart(2, '0')}-31`;
              const daysAgo = Math.floor(
                (new Date(todayIso).getTime() - new Date(endsOn).getTime()) / 86_400_000,
              );
              return (
                <li key={plan.id}>
                  <Link
                    to="/app/plans/$hotelId/$year/$month"
                    params={{ hotelId, year: String(plan.year), month: String(plan.month) }}
                    className="grid grid-cols-[140px_1fr_120px_140px_24px] items-center gap-4 px-5 py-3 opacity-80 transition-colors hover:bg-[var(--app-hover)] hover:opacity-100"
                  >
                    <div className="text-body font-medium text-[var(--ink-700)]">
                      {plan.year}. {HU_MONTHS[plan.month - 1] ?? plan.month}
                    </div>
                    <div className="font-mono text-meta text-[var(--fg-2)]">
                      {startsOn} → {endsOn}
                    </div>
                    <div>
                      <span className="inline-flex items-center rounded-full bg-[var(--app-divider)] px-2 py-0.5 text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
                        {PLAN_STATUS_LABEL[plan.status] ?? plan.status}
                      </span>
                    </div>
                    <div className="text-meta text-[var(--fg-faint)]">
                      {daysAgo === 0
                        ? 'Ma járt le'
                        : daysAgo === 1
                          ? 'Tegnap járt le'
                          : `${daysAgo} napja`}
                    </div>
                    <span className="text-[var(--fg-faint)]" aria-hidden>
                      ›
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/hotels/$hotelId/archive')({
  component: HotelArchivePage,
});
